import {
  ClientProvider,
  GlobalClientConfigProvider,
  queryClient,
  registerQueryErrorHandler,
  unregisterQueryErrorHandler,
  useClient,
  useMutation,
  useQuery
} from '@weenat/client'
import { PossibleError } from '@weenat/client/dist/errors'
import { IntlProvider as WintlProvider } from '@weenat/wintl'
import { str, useLoginAs, useToggleFeature, useToken } from 'app/state'
import * as i18nCountries from 'i18n-iso-countries'
import isNil from 'lodash-es/isNil'
import { default as momentTZ } from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Toaster } from 'sonner'
import { ThemeProvider } from 'styled-components'
import GlobalStyles from './GlobalStyles'
import { makeClient } from './client'
import { CURRENT_ORG_KEY } from './orgProvider'
import useToasts from './src/hooks/useToasts'
import theme from './theme'
import { detectBrowserLanguage } from './utils'

async function dynamicImportBasedOnLocale(locale: string) {
  const all = await Promise.all([
    fetch(`https://cdn.jsdelivr.net/npm/i18n-iso-countries@7.6.0/langs/${locale}.json`)
  ])
  i18nCountries.registerLocale(await all[0].json())
}

const ConnectedIntlProvider: FC = ({ children }) => {
  const [token] = useToken()
  const runtimeClient = useClient()

  const [updateWintlKeys] = useMutation(runtimeClient.wintlMonitoring.update())

  const myProfileRequest = useQuery(runtimeClient.me.getMyProfile(), {
    enabled: !isNil(token)
  })
  const [localeLoaded, setLocaleLoaded] = useState<Record<string, boolean>>({})

  const { language } = myProfileRequest.data || {}
  const locale = language || detectBrowserLanguage()

  useEffect(() => {
    const launchPolyfill = async () => {
      if (!isNil(locale)) {
        await dynamicImportBasedOnLocale(locale)
        setLocaleLoaded({ ...localeLoaded, [locale]: true })
      }
    }

    momentTZ.updateLocale(locale, {
      week: {
        dow: 1 // First day of week is Monday
      }
    })
    launchPolyfill()
  }, [locale])

  const { addErrorToast } = useToasts()

  return (
    <WintlProvider
      locale={locale}
      onUpdateLocaleFail={() => {
        // Can not use intl since the provider is not yet rendered
        addErrorToast('Failed to update language')
      }}
      onKey={!isNil(token) ? updateWintlKeys : undefined}
      showWarns={import.meta.env.DEV}
    >
      {!isNil(locale) && localeLoaded[locale] ? children : null}
    </WintlProvider>
  )
}

const EMPTY_CONFIG = {}

const ClientConfigProviderLogoutAware: FC = ({ children }) => {
  useEffect(() => {
    registerQueryErrorHandler((e) => {
      const error = e as PossibleError
      if ('status' in error && typeof error.status === 'number' && error.status === 401) {
        localStorage.removeItem(CURRENT_ORG_KEY)
        str.flush({ except: ['lightTour', 'features'] })
        queryClient.clear()
        // we don't want to use router here since it may not be initialized at this point
        // eslint-disable-next-line no-restricted-properties
        window.location?.assign('/auth/login')
      }
    })
    return () => unregisterQueryErrorHandler()
  }, [])

  return <GlobalClientConfigProvider config={EMPTY_CONFIG}>{children}</GlobalClientConfigProvider>
}

const ProvidersNeedingGlobalState: FC = ({ children }) => {
  const [features] = useToggleFeature()
  const [loginAs] = useLoginAs()
  const client = useMemo(() => {
    return makeClient(features, loginAs)
  }, [features, loginAs])
  return (
    <HelmetProvider>
      <ClientProvider client={client}>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          <ClientConfigProviderLogoutAware>
            <ConnectedIntlProvider>
              {children}
              <Toaster position='bottom-right' theme='light' />
            </ConnectedIntlProvider>
          </ClientConfigProviderLogoutAware>
        </ThemeProvider>
      </ClientProvider>
    </HelmetProvider>
  )
}
const Providers: FC = ({ children }) => {
  return (
    <str.Providers>
      <ProvidersNeedingGlobalState>{children}</ProvidersNeedingGlobalState>
    </str.Providers>
  )
}

export default Providers
